import React, { useRef } from 'react'
import { Breadcrumbs, MetaData } from 'lib/shared'
import { useProductListPage, LayeredNav, LayeredControls, ProductList } from 'lib/modules/catalog'
import { EmptyContainer, Spinner } from 'lib/ui'
import { Container, Layout } from './styled'

interface Props {
  title: string
  breadcrumbTitle?: string
  categoryId?: number
  loadingState?: boolean
  banner?: React.ReactElement
  infiniteScroll?: boolean
}

export const ProductListLayout = ({
  categoryId,
  title,
  banner,
  breadcrumbTitle,
  loadingState,
  infiniteScroll = true
}: Props) => {
  const categoryTopRef = useRef<HTMLDivElement>(null)

  const scrollTop = () => {
    window.scroll({ top: categoryTopRef.current?.offsetTop ?? 0, left: 0, behavior: 'smooth' })
  }

  const {
    isError,
    isFetching,
    isLoading,
    products,
    layeredNav,
    setFilters,
    infiniteScrollRef,
    infiniteScrollHasNextPage
  } = useProductListPage({
    categoryId,
    enableGTM: true,
    withProducts: true,
    withLayeredNav: true,
    withInfiniteScroll: infiniteScroll
  })

  return (
    <Container>
      <MetaData
        type={categoryId ? 'PLP' : undefined}
        title={categoryId ? undefined : title}
      />

      <h1>{title}</h1>

      <div ref={categoryTopRef} />

      <Breadcrumbs
        pageTitle={breadcrumbTitle}
      />

      {banner}

      {(isLoading || loadingState) ? (
        <EmptyContainer>
          <Spinner />
        </EmptyContainer>
      ) : (
        <>
          <LayeredControls
            isMulti={false}
            isDraft={false}
            categoryId={categoryId}
            showPagination={!infiniteScroll}
          />

          <Layout>
            <LayeredNav
              categoryId={categoryId}
              onFilterChange={scrollTop}
            />

            <div>
              <ProductList
                listPage={categoryId ? 'Category Page' : 'Search Page'}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                products={products}
                clearFiltersHandler={layeredNav?.hasAppliedFilters ? () => setFilters({}) : undefined}
              />

              {infiniteScrollHasNextPage && (
                <Spinner />
              )}

              <div ref={infiniteScrollRef} />
            </div>
          </Layout>

          <LayeredControls
            categoryId={categoryId}
            showSorter={false}
            showTotalCount={false}
            showPagination={!infiniteScroll}
            onChange={scrollTop}
          />
        </>
      )}
    </Container>
  )
}
